import './styles.scss';

import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Button, Calendar, Divider, message, Tooltip, Typography } from 'antd';
import moment, { Moment } from 'moment-timezone';
import { BsCalendar2, BsFillCalendar2CheckFill } from 'react-icons/bs';
import {
  IoChevronBackOutline,
  IoChevronForwardOutline,
  IoGlobeOutline,
} from 'react-icons/io5';
import TMIcon from 'tm-icons-library';

import FeedAPI from '../../api/FeedAPI';
import Assets from '../../assets';
import CustomReadMoreContent from '../../components/CustomReadMoreText/CustomReadMoreContent';
import Header from '../../components/Header/Header';
import { onRefresh } from '../../context/activityReducer';
import { useTheme } from '../../context/ThemeProvider';
import { DATE_FORMATS } from '../../shared/constants';
import showAppError from '../../shared/error';
import { useAppDispatch } from '../../shared/hooks';
import NoDataFound from '../Feed/components/NoDataFound/NoDataFound';
import MangoTitle from '../Feed/Posts/MangoTitle';
import {
  IConsultationPendingEvent,
  IConsultationSlot,
  SlotBookingStatus,
} from './types';

const ICON_SIZE = 64;
const DATE_FORMAT = 'YYYY-MM-DD';
const presentDate = moment().startOf('day').format(DATE_FORMAT);

interface Props {
  isModalVisible: boolean;
  event: IConsultationPendingEvent;
  handleBack: () => void;
  isReschedule?: boolean;
  consultationId?: string;
  refreshPage?: () => void;
}

const BookOneToOneConsultation: React.FC<Props> = ({
  isModalVisible,
  event,
  handleBack,
  isReschedule = false,
  consultationId,
  refreshPage,
}) => {
  const { colors: COLORS } = useTheme();

  const dispatch = useAppDispatch();

  const { title, description, duration, slots, mango, creator } = event;

  const slotsRef = useRef<HTMLDivElement>(null);

  const [bookingState, setBookingState] = useState(SlotBookingStatus.BOOKING);
  const [selectedDay, setSelectedDay] = useState('');
  const [isFetchingAvailableSlots, setIsFetchingAvailableSlots] =
    useState(false);
  const [availableSlots, setAvailableSlots] = useState<IConsultationSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<IConsultationSlot | null>(
    null,
  );
  const [videoCallId, setVideoCallId] = useState<string | null>(null);
  const [isBooking, setIsBooking] = useState(false);

  const showSuccessOrFailedContent = useMemo(
    () =>
      [
        SlotBookingStatus.BOOKING_SUCCESS,
        SlotBookingStatus.BOOKING_FAILED,
        SlotBookingStatus.TEMPORARILY_UNAVAILABLE,
        SlotBookingStatus.NOT_AVAILABLE,
        SlotBookingStatus.FAILED_TO_CHECK_AVAILABILITY,
      ].includes(bookingState),
    [bookingState],
  );

  const nextAvailableSlotDate = useMemo(() => {
    if (slots) {
      return moment(
        slots.find((slot) => {
          return moment(slot?.date)
            .startOf('day')
            .isSameOrAfter(moment().startOf('day'));
        })?.date || presentDate,
      ).format(DATE_FORMAT);
    }

    return presentDate;
  }, [slots]);

  const reset = () => {
    setBookingState(SlotBookingStatus.BOOKING);
    setSelectedDay(nextAvailableSlotDate);
    setSelectedSlot(null);
    setVideoCallId(null);
    setIsFetchingAvailableSlots(false);
    setAvailableSlots([]);
  };

  const resetAfterBookingFailed = () => {
    reset();
    slotsRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const content = useMemo(() => {
    const contentState: {
      title: string;
      subtitle: string;
      subtitle2: string;
      icon: React.ReactNode | null;
      buttonText: string;
      onClick: () => void;
    } = {
      title: '',
      subtitle: '',
      subtitle2: '',
      icon: null,
      buttonText: '',
      onClick: () => {},
    };

    switch (bookingState) {
      case SlotBookingStatus.BOOKING_SUCCESS:
        contentState.title = "You're all set!";
        contentState.subtitle = isReschedule
          ? 'You’ve successfully rescheduled your slot.'
          : 'You’ve successfully booked your slot.';
        contentState.icon = (
          <BsFillCalendar2CheckFill size={ICON_SIZE} color={COLORS.GREEN} />
        );
        contentState.buttonText = 'Okay';
        contentState.onClick = handleBack;
        break;
      case SlotBookingStatus.BOOKING_FAILED:
      case SlotBookingStatus.FAILED_TO_CHECK_AVAILABILITY:
      case SlotBookingStatus.NOT_FOUND:
        contentState.title = 'Booking failed';
        contentState.subtitle = 'Pick your slot and try again.';
        contentState.icon = (
          <TMIcon name="close-circle" size={64} color={COLORS.DANGER} />
        );
        contentState.buttonText = 'Got it';
        contentState.onClick = resetAfterBookingFailed;
        break;
      case SlotBookingStatus.TEMPORARILY_UNAVAILABLE:
        contentState.title = 'Booking failed';
        contentState.subtitle = 'This slot is temporarily unavailable.';
        contentState.subtitle2 = 'Try again in 5 minutes or pick another.';
        contentState.icon = (
          <TMIcon name="close-circle" size={64} color={COLORS.DANGER} />
        );
        contentState.buttonText = 'Got it';
        contentState.onClick = resetAfterBookingFailed;
        break;
      case SlotBookingStatus.NOT_AVAILABLE:
        contentState.title = 'Booking failed';
        contentState.subtitle = 'Oops! That slot was just booked.';
        contentState.subtitle2 = 'Pick another one.';
        contentState.icon = (
          <TMIcon name="close-circle" size={64} color={COLORS.DANGER} />
        );
        contentState.buttonText = 'Got it';
        contentState.onClick = resetAfterBookingFailed;
        break;
      default:
        break;
    }

    return contentState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingState, COLORS.GREEN, COLORS.DANGER]);

  const fetchAvailableSlotsForSelectedDate = async (
    eventId: string,
    { fromTime, toTime }: { fromTime: string; toTime: string },
  ) => {
    if (!eventId || !fromTime || !toTime) return;

    setAvailableSlots([]);
    setIsFetchingAvailableSlots(true);

    try {
      const response = await FeedAPI.getConsultationEventSlots(eventId, {
        fromTime,
        toTime,
      });
      if (response.status === 200) {
        const slotsList = [...(response.data.result || [])]
          .filter((slot) => {
            const isStartTimeInFuture = moment(slot.utcStartTime).isAfter(
              moment(),
            );

            return isStartTimeInFuture;
          })
          .sort((a, b) => moment(a.utcStartTime).diff(moment(b.utcStartTime)));

        setAvailableSlots(slotsList);
      }
    } catch (error) {
      showAppError(error);
    } finally {
      setIsFetchingAvailableSlots(false);
    }
  };

  const bookSlot = async () => {
    if (!selectedSlot) {
      message.warning('Pick a slot to continue');
      return;
    }

    setIsBooking(true);

    try {
      const reqBody: {
        startTime: string;
        endTime: string;
        creator: string;
        eventId: string;
        slotId: string;
        rescheduleSlotFrom?: string;
      } = {
        startTime: selectedSlot?.utcStartTime,
        endTime: selectedSlot?.utcEndTime,
        creator: creator?._id,
        eventId: event?._id,
        slotId: selectedSlot?._id,
      };
      if (isReschedule && !!consultationId) {
        reqBody.rescheduleSlotFrom = consultationId;
      }
      const response = await FeedAPI.checkSlotAvailability(reqBody);

      if (response.status === 200) {
        const slotStatus = response?.data?.result?.status;
        const isSlotAvailable =
          response?.data?.result?.available &&
          slotStatus === SlotBookingStatus.AVAILABLE;

        if (isSlotAvailable) {
          const resp = await FeedAPI.bookConsultationSlot(
            event._id,
            selectedSlot._id,
            moment(selectedSlot.utcStartTime).toISOString(),
            isReschedule && !!consultationId ? consultationId : undefined,
            response?.data?.result?.slotLockId,
          );
          const consultationSlotBookingStatus = resp?.data?.result
            ?.status as SlotBookingStatus;
          const newVideoCallId = resp?.data?.result?.videocall;

          if (resp.status === 200 && newVideoCallId) {
            // show slot booked succesfully content in modal
            setBookingState(SlotBookingStatus.BOOKING_SUCCESS);
            setVideoCallId(newVideoCallId);
            dispatch(onRefresh());
            refreshPage?.();
            // message.success('Slot booked successfully');
          } else if (
            [
              SlotBookingStatus.NOT_FOUND,
              SlotBookingStatus.NOT_AVAILABLE,
            ].includes(consultationSlotBookingStatus)
          ) {
            setBookingState(consultationSlotBookingStatus);
          } else {
            showAppError(resp);
          }
        } else {
          // show failed to book slot content in modal based on slot status: TEMPORARILY_UNAVAILABLE, NOT_AVAILABLE, FAILED_TO_CHECK_AVAILABILITY
          setBookingState(slotStatus as SlotBookingStatus);
        }
      }
    } catch (err: any) {
      setBookingState(SlotBookingStatus.BOOKING_FAILED);
    } finally {
      setIsBooking(false);
    }
  };

  const addToCalendarHandler = async () => {
    if (!videoCallId) return;

    try {
      const resp = await FeedAPI.addToCalendar(videoCallId);
      if (resp.status === 200) {
        window.open(resp?.data?.result);
      }
    } catch (error) {
      showAppError(error);
    }
  };

  const handleChangeDate = (date: Moment) => {
    setSelectedDay(date.format(DATE_FORMAT));
  };

  const markedDates = useMemo(() => {
    const dates: Record<string, { selected?: boolean; marked?: boolean }> = {};

    if (selectedDay) {
      dates[selectedDay] = { selected: true };
    }

    if (slots) {
      slots.forEach((slot) => {
        const key = moment(slot.utcStartTime).format(DATE_FORMAT);
        dates[key] = {
          ...(dates[key] || {}),
          marked: true,
        };
      });
    }

    return dates;
  }, [selectedDay, slots]);

  // const availableSlots = useMemo(() => {
  //   let daySlots: IConsultationSlot[] = [];

  //   if (selectedDay) {
  //     daySlots = slots.filter((slot) => {
  //       return moment(slot.utcStartTime).format(DATE_FORMAT) === selectedDay;
  //     });
  //   }

  //   return daySlots;
  // }, [selectedDay, slots]);

  const disableDate = (current: Moment) => {
    return current < moment().startOf('day');
  };

  useEffect(() => {
    if (selectedDay) {
      setSelectedSlot(null);
      fetchAvailableSlotsForSelectedDate(event._id, {
        fromTime: moment(selectedDay).startOf('day').toISOString(),
        toTime: moment(selectedDay).endOf('day').toISOString(),
      });
      slotsRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedDay, event._id]);

  useEffect(() => {
    if (isModalVisible) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalVisible, nextAvailableSlotDate]);

  const customMonthHeader = ({
    value,
    onChange,
  }: {
    value: Moment;
    onChange: (value: Moment) => void;
  }) => {
    // this will render month with year along with prev and next button

    const year = value.year();
    const month = value.month();
    const months = moment.months();
    const currentMonth = months[month];
    const currentYear = year;

    const onMonthChange = (newMonth: number) => {
      const now = value.clone().month(newMonth);
      onChange(now);
    };

    return (
      <div className="custom-calendar-header">
        <Button
          onClick={() => onMonthChange(month - 1)}
          className="arrow-btn"
          icon={<IoChevronBackOutline size={24} />}
          disabled={moment(value)
            .startOf('day')
            .subtract(1, 'month')
            .isBefore(moment().startOf('day'))}
        />

        <Typography.Title
          level={5}
          style={{ margin: 0 }}
          className="month-title">
          {currentMonth} {currentYear}
        </Typography.Title>

        <Button
          onClick={() => onMonthChange(month + 1)}
          className="arrow-btn"
          icon={<IoChevronForwardOutline size={24} />}
        />
      </div>
    );
  };

  return (
    <>
      <div className="postdetails__container bookslot__container">
        <Header
          title={isReschedule ? 'Reschedule your slot' : 'Book your slot'}
          handleBack={handleBack}
        />

        <div className="scroll__container">
          {(bookingState === SlotBookingStatus.BOOKING && (
            <>
              <h3 className="event-title">{title}</h3>

              <div className="subtitle">
                <MangoTitle mangoes={[mango as any]}>
                  <span className="time">{duration} minutes</span>{' '}
                  <span className="dot">•</span>{' '}
                </MangoTitle>
              </div>

              {description ? (
                <CustomReadMoreContent
                  formatType="parsed"
                  lines={2}
                  className="mb-0"
                  seeMoreText="Read more"
                  seeLessText="Read less">
                  {description}
                </CustomReadMoreContent>
              ) : null}

              <Divider className="divider-border" />

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBlock: 12,
                }}>
                <h4 className="label">Select date & time</h4>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                  <IoGlobeOutline />

                  <Typography.Text style={{ margin: 0, marginLeft: 6 }}>
                    {
                      moment.tz(moment.tz.guess()).zoneAbbr() // get timezone
                    }
                  </Typography.Text>
                </div>
              </div>

              <Calendar
                fullscreen={false}
                mode="month"
                headerRender={customMonthHeader}
                dateCellRender={(date) => {
                  const dateString = date.format(DATE_FORMAT);
                  const isPastDate = date.isBefore(moment().startOf('day'));

                  if (markedDates[dateString]?.marked) {
                    return (
                      <div
                        className="date-active-dot"
                        style={{
                          opacity: isPastDate ? 0.5 : 1,
                          filter: isPastDate ? 'grayscale(1)' : 'none',
                        }}
                      />
                    );
                  }

                  return null;
                }}
                disabledDate={disableDate}
                value={moment(selectedDay)}
                onChange={handleChangeDate}
              />

              {selectedDay ? (
                <>
                  <h4
                    className="label"
                    style={{
                      marginTop: 24,
                      marginBottom: 8,
                    }}>
                    Available slots
                  </h4>

                  {(isFetchingAvailableSlots && (
                    <Typography.Text>
                      Loading...
                      {/* <Loader
                        spinning
                        style={{
                          flexShrink: 0,
                          marginLeft: 12,
                          fontSize: 14,
                        }}
                      /> */}
                    </Typography.Text>
                  )) ||
                    (availableSlots.length === 0 && (
                      <Typography.Paragraph className="mb-0">
                        No slots available on{' '}
                        <b style={{ fontWeight: 500 }}>
                          {moment(selectedDay).format(
                            DATE_FORMATS.DAY_YEAR_FORMAT,
                          )}
                          .
                        </b>
                      </Typography.Paragraph>
                    )) || (
                      <div className="slots-list">
                        {availableSlots.map((slot) => {
                          const isSelected =
                            slot._id === selectedSlot?._id &&
                            moment(slot.utcStartTime).format(DATE_FORMAT) ===
                              selectedDay;
                          const isSlotAvailable =
                            slot?.available && !slot?.locked;

                          return (
                            <Tooltip
                              key={`${slot?._id}-tooltip`}
                              title={
                                (slot?.locked &&
                                  'Slot temporarily unavailable. Try again in 5 minutes or pick another.') ||
                                (!slot?.available &&
                                  'This slot is booked. Pick another one.') ||
                                ''
                              }>
                              <div
                                className={`slots-list__item ${
                                  isSelected ? 'selected' : ''
                                } ${isSlotAvailable ? '' : 'not-available'}`}
                                key={slot?._id}
                                onClick={() => {
                                  if (
                                    !slot?.subscriptionEligibility
                                      ?.allowedToBook
                                  ) {
                                    message.warning(
                                      slot?.subscriptionEligibility?.message,
                                    );
                                    return;
                                  }
                                  if (!isSelected && isSlotAvailable) {
                                    setSelectedSlot(slot);
                                  }
                                  // else if (isSelected) {
                                  //   setSelectedSlot(null);
                                  // }
                                }}
                                role="button"
                                tabIndex={0}>
                                {`${moment(slot.utcStartTime).format(
                                  DATE_FORMATS.TIME_FORMAT_FULL,
                                )} - ${moment(slot.utcEndTime).format(
                                  DATE_FORMATS.TIME_FORMAT_FULL,
                                )}`}
                              </div>
                            </Tooltip>
                          );
                        })}
                      </div>
                    )}
                </>
              ) : null}
            </>
          )) ||
            (showSuccessOrFailedContent && (
              <>
                <NoDataFound
                  iconElement={content.icon}
                  title={content.title}
                  subtitle={content.subtitle}
                  subtitle2={content?.subtitle2}
                  style={{ width: '100%' }}
                />

                {bookingState === SlotBookingStatus.BOOKING_SUCCESS && (
                  <div className="booked-slot-content d-flex align-center justify-between">
                    <div className="slot-details d-flex align-center">
                      <BsCalendar2 size={16} />

                      {moment(selectedDay).format(DATE_FORMATS.DAY_YEAR_FORMAT)}

                      {', '}

                      {`${moment(selectedSlot?.utcStartTime).format(
                        DATE_FORMATS.TIME_FORMAT_FULL,
                      )} - ${moment(selectedSlot?.utcEndTime).format(
                        DATE_FORMATS.TIME_FORMAT_FULL,
                      )}`}
                    </div>

                    <Button
                      className="siteBtn grayBtn"
                      icon={
                        <img
                          src={Assets.svg.googleCalendarIcon}
                          alt="Google Calendar"
                          style={{ marginRight: 6 }}
                        />
                      }
                      onClick={addToCalendarHandler}>
                      Add to Google Calendar
                    </Button>
                  </div>
                )}
              </>
            ))}
        </div>

        <div className="bookslot__footer">
          {(bookingState === SlotBookingStatus.BOOKING && (
            <Button
              className="siteBtn siteBtnPrimary"
              loading={isBooking}
              disabled={!selectedSlot}
              onClick={bookSlot}
              block>
              Book now
            </Button>
          )) ||
            (showSuccessOrFailedContent && (
              <Button
                className={`siteBtn ${
                  bookingState === SlotBookingStatus.BOOKING_SUCCESS
                    ? 'siteBtnPrimary'
                    : 'grayBtn'
                }`}
                onClick={content.onClick}
                block>
                {content.buttonText}
              </Button>
            ))}
        </div>
      </div>
    </>
  );
};

export default BookOneToOneConsultation;
